
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { User, Settings, LogOut, BookOpen, FileText, Menu, ArrowLeft, Building2, LayoutDashboard, FileUser, Mic, Compass, Newspaper, Briefcase, X } from "lucide-react";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { useIsAffiliate } from "@/hooks/useIsAffiliate";
import { useInstitutionAdmin } from "@/hooks/useInstitutionAdmin";

const MainNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: isAffiliate } = useIsAffiliate();
  const { isInstitutionAdmin } = useInstitutionAdmin();
  
  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };
  
  const isResumeBuilder = location.pathname.includes("/resume-builder");
  const isEditor = location.pathname.includes("/resume-builder/editor/");

  return <nav className="flex items-center space-x-6">
      <Link to="/home" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/home") ? "text-primary" : "text-foreground/60"}`}>
        Dashboard
      </Link>
      
      <Link to="/resume-builder" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/resume-builder") ? "text-primary" : "text-foreground/60"}`}>
        Resume Builder
      </Link>
      
      <Link to="/interview" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/interview") ? "text-primary" : "text-foreground/60"}`}>
        Interview
      </Link>
      
      <Link to="/career-discovery" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/career-discovery") ? "text-primary" : "text-foreground/60"}`}>
        Career Discovery
      </Link>
      
      <Link to="/blog" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/blog") ? "text-primary" : "text-foreground/60"}`}>
        Blog
      </Link>
      
      <Link to="/careers" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/careers") ? "text-primary" : "text-foreground/60"}`}>
        Careers
      </Link>
      
      {isAffiliate && (
        <Link to="/affiliate/dashboard" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/affiliate/dashboard") ? "text-primary" : "text-foreground/60"}`}>
          Affiliate Dashboard
        </Link>
      )}
      
      {isInstitutionAdmin && (
        <Link to="/institution/dashboard" className={`text-body font-semantic-medium transition-colors hover:text-primary ${isActive("/institution/dashboard") ? "text-primary" : "text-foreground/60"}`}>
          Institution Admin
        </Link>
      )}
    </nav>;
};

export function Header() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: isAffiliate } = useIsAffiliate();
  const { isInstitutionAdmin } = useInstitutionAdmin();
  const [isOpen, setIsOpen] = useState(false);
  
  const isEditor = location.pathname.includes("/resume-builder/editor/");
  
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      try {
        localStorage.removeItem('sb-jknbrmgwzizdtvtnufwb-auth-token');
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }
      window.location.href = "/";
    } catch (error) {
      console.error("Signout error:", error);
      try {
        localStorage.removeItem('sb-jknbrmgwzizdtvtnufwb-auth-token');
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }
      window.location.href = "/";
    }
  };
  
  const handleBackToResumes = () => {
    navigate("/resume-builder");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        {/* Logo */}
        <Link to="/" className="mr-4 flex items-center space-x-2">
          {/* Updated to correct user-provided PNG */}
          <img
            src="/lovable-uploads/839db717-34f8-460a-9cdf-c19453781014.png"
            alt="Careerfy logo"
            className="h-8 w-auto"
          />
        </Link>

        {/* If in editor mode, show back button */}
        {isEditor ? (
          <Button 
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 mr-auto"
            onClick={handleBackToResumes}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Resumes</span>
          </Button>
        ) : (
          <>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6 ml-6">
              <MainNav />
            </nav>

            {/* Mobile Navigation */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden ml-2">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <SheetHeader className="p-4 border-b">
                  <SheetTitle className="flex items-center gap-2">
                    <img
                      src="/lovable-uploads/839db717-34f8-460a-9cdf-c19453781014.png"
                      alt="Careerfy logo"
                      className="h-6 w-auto"
                    />
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col py-4">
                  <Link 
                    to="/home" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname === "/home" ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <LayoutDashboard className="h-5 w-5" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/resume-builder" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/resume-builder") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <FileUser className="h-5 w-5" />
                    Resume Builder
                  </Link>
                  <Link 
                    to="/interview" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/interview") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <Mic className="h-5 w-5" />
                    Interview
                  </Link>
                  <Link 
                    to="/career-discovery" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/career-discovery") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <Compass className="h-5 w-5" />
                    Career Discovery
                  </Link>
                  
                  <div className="my-2 mx-4 border-t" />
                  
                  <Link 
                    to="/blog" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/blog") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <Newspaper className="h-5 w-5" />
                    Blog
                  </Link>
                  <Link 
                    to="/careers" 
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/careers") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                  >
                    <Briefcase className="h-5 w-5" />
                    Careers
                  </Link>
                  
                  {(isAffiliate || isInstitutionAdmin) && (
                    <>
                      <div className="my-2 mx-4 border-t" />
                      {isAffiliate && (
                        <Link 
                          to="/affiliate/dashboard" 
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/affiliate") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                        >
                          <FileText className="h-5 w-5" />
                          Affiliate Dashboard
                        </Link>
                      )}
                      {isInstitutionAdmin && (
                        <Link 
                          to="/institution/dashboard" 
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted ${location.pathname.startsWith("/institution") ? "bg-primary/10 text-primary border-r-2 border-primary" : "text-foreground/70 hover:text-foreground"}`}
                        >
                          <Building2 className="h-5 w-5" />
                          Institution Admin
                        </Link>
                      )}
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </>
        )}

        {/* Auth Section - Always visible regardless of page */}
        <div className="flex items-center ml-auto space-x-4">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url} alt={profile?.full_name || user.email} />
                    <AvatarFallback>
                      {profile?.full_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-body font-semantic-medium leading-none">{profile?.full_name}</p>
                    <p className="text-fine leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/profile")} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/settings")} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                {isAffiliate && (
                  <DropdownMenuItem onSelect={() => navigate("/affiliate/dashboard")} className="cursor-pointer">
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Affiliate Dashboard</span>
                  </DropdownMenuItem>
                )}
                {isInstitutionAdmin && (
                  <DropdownMenuItem onSelect={() => navigate("/institution/dashboard")} className="cursor-pointer">
                    <Building2 className="mr-2 h-4 w-4" />
                    <span>Institution Admin</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleSignOut} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild className="bg-primary hover:bg-primary/90 text-white">
              <Link to="/auth">Sign In</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
